import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { isElement } from "./guards";

const focusableElemSelector = [
    "a[href]:not([disabled])",
    "button:not([disabled])",
    "textarea:not([disabled])",
    'input[type="text"]:not([disabled])',
    'input[type="radio"]:not([disabled])',
    'input[type="checkbox"]:not([disabled])',
    'input[type="email"]:not([disabled])',
    'input[type="submit"]:not([disabled])',
    "select:not([disabled])",
    "label[for]",
].join(", ");

export const getFocusableElems = (parent: HTMLElement): HTMLElement[] => {
    const window = getWindow();
    if (!window) {
        return [];
    }
    const elems = Array.from(
        parent.querySelectorAll<HTMLElement>(focusableElemSelector),
    ).filter((el) => {
        // Check if element is visible
        const style = window.getComputedStyle(el);
        return (
            style.getPropertyValue("visibility") !== "hidden" &&
            style.getPropertyValue("display") !== "none" &&
            el.offsetParent !== null
        );
    });
    // Sort by DOM order
    return elems.sort((a, b) => {
        const position = a.compareDocumentPosition(b);
        if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
    });
};

export const trapFocus = (element: HTMLElement): (() => void) => {
    const document = getDocument();
    const focusableElems = getFocusableElems(element);
    const firstFocusableEl = focusableElems[0];
    const lastFocusableEl = focusableElems[focusableElems.length - 1];

    const handleKeyDown = (event: KeyboardEvent) => {
        if (!document) {
            return;
        }
        const isTabPressed = event.key === "Tab";

        if (!isTabPressed) {
            return;
        }

        /* shift + tab */
        if (event.shiftKey) {
            if (document.activeElement === firstFocusableEl) {
                focusElement(lastFocusableEl);
                event.preventDefault();
            }
            /* tab */
        } else {
            if (document.activeElement === lastFocusableEl) {
                focusElement(firstFocusableEl);
                event.preventDefault();
            }
        }
    };

    element.addEventListener("keydown", handleKeyDown);

    // Return cleanup function
    return () => {
        element.removeEventListener("keydown", handleKeyDown);
    };
};

export const trapMenuFocus = (
    menuElem: HTMLElement,
    closeLinkElem: HTMLElement,
): (() => void) => {
    const document = getDocument();
    const focusableElems = getFocusableElems(menuElem);
    const firstFocusableEl = focusableElems[0];
    const lastFocusableEl = focusableElems[focusableElems.length - 1];

    const handleCloseKeyDown = (event: KeyboardEvent) => {
        if (!document) {
            return;
        }
        const isTabPressed = event.key === "Tab";

        if (!isTabPressed) {
            return;
        }

        /* shift + tab */
        if (event.shiftKey) {
            if (document.activeElement === closeLinkElem) {
                focusElement(lastFocusableEl);
                event.preventDefault();
            }
            /* tab */
        } else {
            if (document.activeElement === closeLinkElem) {
                focusElement(firstFocusableEl);
                event.preventDefault();
            }
        }
    };

    const handleMenuKeyDown = (event: KeyboardEvent) => {
        if (!document) {
            return;
        }
        const isTabPressed = event.key === "Tab";

        if (!isTabPressed) {
            return;
        }

        /* shift + tab */
        if (event.shiftKey) {
            if (document.activeElement === firstFocusableEl) {
                focusElement(closeLinkElem);
                event.preventDefault();
            }
            /* tab */
        } else {
            if (document.activeElement === lastFocusableEl) {
                focusElement(closeLinkElem);
                event.preventDefault();
            }
        }
    };

    closeLinkElem.addEventListener("keydown", handleCloseKeyDown);
    menuElem.addEventListener("keydown", handleMenuKeyDown);

    // Return cleanup function
    return () => {
        closeLinkElem.removeEventListener("keydown", handleCloseKeyDown);
        menuElem.removeEventListener("keydown", handleMenuKeyDown);
    };
};

export const focusElement = (
    selector: HTMLElement | string,
    options?: FocusOptions,
) => {
    const document = getDocument();
    if (!document) {
        return;
    }
    const elem = isElement(selector)
        ? selector
        : document.querySelector<HTMLElement>(selector);
    if (elem) {
        console.trace(`Changing keyboard focus to ${selector}.`);
        elem.focus(options);
    } else {
        console.trace(
            `Attempted to change keyboard focus to ${selector}, but no matching element exists.`,
        );
    }
};
